import React, { useCallback, useRef } from 'react'
import {
  ArrowLeft as LuArrowLeft,
  Calendar as LuCalendar,
  Clock as LuClock,
  ExternalLink,
  Info as LuInfo,
  MapPin as LuMapPin,
  Users as LuUsers,
} from 'lucide-react'
import {
  Activity,
  ArrowLeft,
  BarChart2,
  Calendar,
  Clock,
  Download,
  Info,
  MapPin,
  Play,
  Printer,
  SbIcon,
  Target,
  Users,
  Video,
} from '@/components/icons/StatboxIcons'
import { SAMPLE_GAMES } from './matchPreviewSamples'
import { usePreviewAutoplay } from './usePreviewAutoplay'
import { HintBubble, PreviewHintStyles } from './PreviewHint'

/**
 * Scaled copy of the community portal match page: CommunityLayout header,
 * collapsed CommunitySidebar, PostGameHub, then the CommunityMatchDetails
 * report (score card, roster, goal scorers, highlights, events, game
 * statistics, player performance). Class names follow the source files;
 * the page is laid out at DESIGN_WIDTH and zoomed to fit.
 */

const DESIGN_WIDTH = 680
const MANAGER = 'Karl Haddad'

const PRIMARY_NAV = [
  ['overview', 'Overview'],
  ['matches', 'Matches', true],
  ['discovery', 'Discovery'],
  ['competitions', 'Competitions'],
  ['leaderboard', 'Leaderboards'],
  ['challenges', 'Challenges'],
  ['library', 'Video Library'],
]
const BOTTOM_NAV = [['management', 'Management'], ['settings', 'Settings']]

const TEAM_METRICS = {
  football: [
    ['goals', 'Goals'], ['assists', 'Assists'], ['shots_on_target', 'Shots on target'], ['shots_off_target', 'Shots off target'],
    ['chances_created', 'Chances created'], ['successful_dribbles', 'Successful dribbles'], ['successful_tib', 'Successful TIB'], ['saves', 'Saves'],
  ],
  basketball: [
    ['fg_made', 'Field Goals', 'fg_attempted'], ['three_pts_made', '3-Pointers', 'three_pts_attempted'], ['assists', 'Assists'],
    ['offensive_rebound', 'Offensive Rebounds'], ['defensive_rebound', 'Defensive Rebounds'], ['blocks', 'Blocks'], ['turnover', 'Turnovers'],
    ['free_throws_scored', 'Free Throws', 'free_throws_attempted'],
  ],
  padel: [
    ['points', 'Points'], ['winner', 'Winners'], ['ace', 'Aces'], ['serve', 'Serve Points'], ['return', 'Return Points'],
    ['smash', 'Smashes'], ['lob', 'Lobs'], ['dropshot', 'Dropshots'], ['unforced_errors', 'Unforced Errors'],
  ],
}

/** [key, shortLabel, emphasize] — PLAYER_METRIC_COLUMNS_* in CommunityMatchDetails. */
const PLAYER_COLUMNS = {
  football: [
    ['goals', 'G', true], ['assists', 'A', true], ['shots_on_target', 'SOT'], ['shots_off_target', 'SOF'], ['chances_created', 'CH'],
    ['successful_dribbles', 'DRB'], ['successful_tib', 'TIB'], ['saves', 'SV', true], ['grade', 'GRD', true],
  ],
  basketball: [
    ['fg_made', 'FGM', true], ['three_pts_made', '3PM', true], ['assists', 'A', true], ['offensive_rebound', 'OREB'], ['defensive_rebound', 'DREB'],
    ['blocks', 'BLK'], ['turnover', 'TO'], ['free_throws_scored', 'FTM'], ['grade', 'GRD', true],
  ],
  padel: [
    ['winner', 'WIN', true], ['ace', 'ACE', true], ['serve', 'SRV'], ['return', 'RET'], ['smash', 'SMA'], ['lob', 'LOB'],
    ['dropshot', 'DRP'], ['unforced_errors', 'UE'], ['grade', 'GRD', true],
  ],
}

const SAMPLE_KEY = {
  three_pts_made: 'three_made',
  three_pts_attempted: 'three_attempted',
  free_throws_scored: 'ft_made',
  free_throws_attempted: 'ft_attempted',
  turnover: 'turnovers',
}

const EVENT_KEYS = {
  Goal: 'goals', Dribble: 'successful_dribbles', Save: 'saves', Assist: 'assists', Three: 'three_pts_made', Block: 'blocks',
  Rebound: 'defensive_rebound', Ace: 'ace', Winner: 'winner', Smash: 'smash', Lob: 'lob', Dropshot: 'dropshot', Rally: 'long_rally',
}

/** getEventInfo in CommunityMatchDetails. */
const EVENT_INFO = {
  goals: ['⚽', 'bg-green-100'],
  assists: ['🎯', 'bg-blue-100'],
  saves: ['🛡️', 'bg-[#F2F3F5]'],
  successful_tib: ['⚡', 'bg-yellow-100'],
  successful_dribbles: ['🏃', 'bg-orange-100'],
  chances_created: ['💡', 'bg-[#EEF1F8]'],
  shots_on_target: ['🎯', 'bg-red-100'],
}

const EVENT_MINUTES = {
  basketball: [3, 8, 14, 19, 24, 29, 33, 38],
  padel: [4, 13, 22, 31, 41, 52, 66],
}

const AVATAR_GRADIENTS = [
  'from-blue-500 to-blue-600', 'from-purple-500 to-purple-600', 'from-pink-500 to-pink-600', 'from-red-500 to-red-600',
  'from-orange-500 to-orange-600', 'from-amber-500 to-amber-600', 'from-yellow-500 to-yellow-600', 'from-green-500 to-green-600',
  'from-emerald-500 to-emerald-600', 'from-teal-500 to-teal-600', 'from-cyan-500 to-cyan-600', 'from-indigo-500 to-indigo-600',
]

const PROFILE_LINES = ['180 cm • Right', '176 cm • Left', '183 cm • Right', '172 cm • Right', '178 cm • Both']

function avatarGradient(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return `bg-gradient-to-br ${AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]}`
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

function statOf(sport, key, s) {
  if (sport === 'football' && key === 'shots_on_target') return num(s.shots_on_target) + num(s.goals)
  if (sport === 'padel' && key === 'points') return num(s.serve) + num(s.return)
  return num(s[SAMPLE_KEY[key] || key])
}

function fmtDate(d) {
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function fmtTime(t) {
  const [h, m] = t.substring(0, 5).split(':')
  const hr = parseInt(h, 10)
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`
}

function fmtTimestamp(d) {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const prefix = d.toDateString() === now.toDateString()
    ? 'Today'
    : d.toDateString() === yesterday.toDateString()
      ? 'Yesterday'
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${prefix}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
}

function at(date, dayOffset, time) {
  const d = new Date(`${date}T${time}:00`)
  d.setDate(d.getDate() + dayOffset)
  return d
}

function minuteOf(sport, label, index) {
  if (EVENT_MINUTES[sport]) return EVENT_MINUTES[sport][index] ?? index * 5
  return parseInt(label, 10)
}

function buildView(game) {
  const { sport, players } = game
  const pending = new Set(players.length > 4 ? [3, 8] : [3])
  const roster = players.map((p, i) => ({ ...p, claimed: !pending.has(i) }))
  const claimedCount = roster.filter((p) => p.claimed).length
  const events = game.events.map(([label, type, , name, side], i) => ({
    minute: minuteOf(sport, label, i),
    key: EVENT_KEYS[type] || type.toLowerCase(),
    name,
    side,
  }))
  const totals = (side) => {
    const out = {}
    for (const [key, , attempted] of TEAM_METRICS[sport]) {
      for (const k of [key, attempted].filter(Boolean)) {
        out[k] = players.filter((p) => p.side === side).reduce((acc, p) => acc + statOf(sport, k, p.s), 0)
      }
    }
    return out
  }
  const section = (side, title) => {
    const rows = players.filter((p) => p.side === side)
    const max = {}
    for (const [key] of PLAYER_COLUMNS[sport]) {
      max[key] = Math.max(...rows.map((p) => (key === 'grade' ? p.rating : statOf(sport, key, p.s))))
    }
    const avg = rows.reduce((acc, p) => acc + p.rating, 0) / rows.length
    return { title, rows, max, avg }
  }
  const [scoreA, scoreB] = game.score.split('-').map((v) => v.trim())
  const activity = [
    ['success', `${claimedCount}/${players.length} players claimed stats`, at(game.datePlayed, 1, '12:00')],
    ['info', 'Analysis complete', at(game.datePlayed, 1, '08:00')],
    ['info', 'Footage uploaded', at(game.datePlayed, 0, '23:30')],
    ['success', `${players.length}/${players.length} spots confirmed`, at(game.datePlayed, -1, '18:00')],
    ['info', 'Game created', at(game.datePlayed, -5, '15:30')],
  ]
  return {
    roster,
    claimedCount,
    events,
    totalsA: totals('A'),
    totalsB: totals('B'),
    sections: [section('A', game.home), section('B', game.away)],
    scoreA,
    scoreB,
    activity,
    goals: {
      A: events.filter((e) => e.key === 'goals' && e.side === 'A'),
      B: events.filter((e) => e.key === 'goals' && e.side === 'B'),
    },
  }
}

const VIEWS = Object.fromEntries(Object.entries(SAMPLE_GAMES).map(([k, g]) => [k, buildView(g)]))

function PortalHeader() {
  return (
    <header className="z-50 shrink-0 bg-[#1E2A3C]">
      <div className="flex h-14 min-w-0 items-center justify-between gap-2 px-5">
        <div className="flex min-w-0 items-center gap-2">
          <img src="/logo_white.png" alt="" className="h-3.5 w-auto" />
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <div className="flex shrink-0 items-center gap-1 rounded-lg border border-white/15 bg-white/10 py-1 pl-1.5 pr-1">
            <SbIcon name="coins" size={14} className="shrink-0 text-white" />
            <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-white">12</span>
            <span className="pr-0.5 text-[10px] font-medium text-white/70">credits</span>
            <span className="whitespace-nowrap rounded-md bg-[#3B82F6] px-2 py-0.5 text-[10px] font-bold text-white">Buy</span>
          </div>
          <span className="flex items-center gap-1.5 rounded-lg bg-[#3B82F6] px-3 py-1.5 text-xs font-semibold text-white">
            <SbIcon name="create" size={14} />
            <span>Create game</span>
          </span>
          <span className="relative rounded-lg p-2">
            <SbIcon name="bell" size={18} className="text-white" />
          </span>
        </div>
      </div>
    </header>
  )
}

function PortalSidebar({ communityName }) {
  const item = ([icon, label, active]) => (
    <span key={icon} title={label} className="relative flex w-full items-center justify-center rounded-lg p-2.5">
      <SbIcon name={icon} size={18} className={`flex-shrink-0 ${active ? 'text-[#2F9E44]' : 'text-[#8A909A]'}`} />
    </span>
  )
  return (
    <aside className="flex w-[72px] shrink-0 flex-col border-r border-[#E7E7EA] bg-[#F7F8FA] text-[#111111]">
      <div className="flex-shrink-0 border-b border-[#ECEDEF] p-3">
        <div className="flex items-center justify-center">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#D7F3E3]">
            <span className="text-sm font-bold text-[#1B8A45]">{communityName.charAt(0).toUpperCase()}</span>
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-hidden p-2">{PRIMARY_NAV.map(item)}</nav>
      <div className="flex-shrink-0 space-y-0.5 px-2 pb-1">
        <div className="mx-2 my-4"><div className="border-t border-[#ECEDEF]" /></div>
        {BOTTOM_NAV.map(item)}
      </div>
      <div className="flex-shrink-0 border-t border-[#ECEDEF]">
        <div className="flex flex-col gap-2 p-2">
          <span className="flex w-full items-center justify-center rounded-lg p-2">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#D7F3E3]">
              <span className="text-xs font-bold text-[#1B8A45]">{MANAGER.split(' ').map((w) => w.charAt(0)).join('')}</span>
            </span>
          </span>
          <span className="flex w-full items-center justify-center rounded-lg p-2 text-[#8A909A]">
            <SbIcon name="chevronRight" size={16} />
          </span>
        </div>
      </div>
    </aside>
  )
}

function ClaimBadge({ claimed }) {
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
        claimed ? 'border border-emerald-200 bg-emerald-50 text-emerald-700' : 'border border-amber-200 bg-amber-50 text-amber-700'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${claimed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      {claimed ? 'Stats claimed' : 'Pending claim'}
    </span>
  )
}

function RosterList({ roster, grid }) {
  return (
    <div className={grid ? 'grid grid-cols-2 gap-2' : 'space-y-1'}>
      {roster.map((p) => (
        <div key={p.name} className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-indigo-500 text-xs font-semibold text-white">
            {p.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{p.name}</p>
          </div>
          <ClaimBadge claimed={p.claimed} />
        </div>
      ))}
    </div>
  )
}

function PostGameHub({ game, view }) {
  const sportName = game.sport.charAt(0).toUpperCase() + game.sport.slice(1)
  const details = [
    ['Sport', sportName],
    ['Game Type', 'Friendly'],
    ['Date & Time', `${fmtDate(game.datePlayed)} · ${fmtTime(game.hourSlot)}`],
    ['Total Spots', String(game.players.length)],
    ['Published', 'No — private to your community'],
  ]
  return (
    <div className="p-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6">
          <div className="absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-64 w-64 -translate-x-1/2 translate-y-1/2 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="relative z-10 flex flex-row items-start justify-between gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                  <span className="text-3xl">{sportName === 'Basketball' ? '🏀' : sportName === 'Football' ? '⚽' : '🏟️'}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-3xl font-bold text-white">{sportName}</h1>
                    <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">Friendly</span>
                  </div>
                  <p className="mt-0.5 text-base font-medium text-indigo-200">Game Management</p>
                </div>
              </div>
              <div className="space-y-1 pl-0.5">
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <LuCalendar className="h-4 w-4 flex-shrink-0" />
                  <span>{fmtDate(game.datePlayed)} · {fmtTime(game.hourSlot)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <LuMapPin className="h-4 w-4 flex-shrink-0" />
                  <span>{game.venueName} · {game.venue}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-col items-end gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/20 px-3 py-1.5 text-xs font-semibold text-emerald-100 backdrop-blur-sm">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                Completed
              </span>
              <p className="text-sm text-white/70">{game.players.length} players</p>
            </div>
          </div>
        </div>

        <div className="-mt-2 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
          <span className="flex items-center gap-1.5 text-sm font-medium text-indigo-600">
            <LuArrowLeft className="h-4 w-4" />
            Back to Matches
          </span>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LuUsers className="h-5 w-5 text-slate-700" />
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Players</h3>
            </div>
            <p className="text-sm text-slate-500">{game.players.length} players</p>
          </div>
          <p className="mb-3 text-xs text-slate-400">
            {view.claimedCount} of {game.players.length} have claimed their stats
          </p>
          <RosterList roster={view.roster} />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-2">
            <LuInfo className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Game Details</h3>
          </div>
          <div className="flex flex-row gap-5">
            <div className="w-[40%] flex-shrink-0">
              <div
                className="relative overflow-hidden rounded-xl border border-slate-200"
                style={{ height: 210, background: 'linear-gradient(135deg, #e0ecf8 0%, #d5e3f0 30%, #c8dce8 60%, #dde8f0 100%)' }}
              >
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{ backgroundImage: 'radial-gradient(circle, #94a3b8 1px, transparent 1px)', backgroundSize: '18px 18px' }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-500 shadow-lg">
                    <LuMapPin className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-slate-700">{game.venueName}</p>
                  <p className="text-xs text-slate-500">{game.venue}</p>
                </div>
                <span className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-indigo-600 shadow-sm backdrop-blur-sm">
                  Get Directions
                  <ExternalLink className="h-3 w-3" />
                </span>
              </div>
            </div>
            <div className="flex-1 space-y-3">
              {details.map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 text-sm">
                  <span className="flex-shrink-0 text-slate-400">{label}</span>
                  <span className={`text-right font-medium ${label === 'Published' ? 'text-slate-400' : 'text-slate-700'}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <LuClock className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Activity</h3>
          </div>
          <div className="relative pl-6">
            <div className="absolute bottom-2 left-[9px] top-2 w-px bg-slate-200" />
            {view.activity.map(([type, text, when]) => (
              <div key={text} className="relative flex items-start gap-3 pb-5 last:pb-0">
                <div className={`absolute left-[-15px] mt-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white ${type === 'success' ? 'bg-emerald-500' : 'bg-indigo-500'}`}>
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <div className="flex min-w-0 flex-1 items-start justify-between gap-4">
                  <p className="text-sm text-slate-700">{text}</p>
                  <p className="flex-shrink-0 whitespace-nowrap text-xs text-slate-400">{fmtTimestamp(when)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function SectionIcon({ icon: Icon, tone }) {
  return (
    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl shadow-md ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
  )
}

function MatchTeamCrest({ name, align }) {
  return (
    <div className={`flex min-w-0 items-center gap-2.5 ${align === 'right' ? 'flex-row-reverse text-right' : 'text-left'}`}>
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
        <span className="text-sm font-bold text-gray-500">{name.charAt(0).toUpperCase()}</span>
      </div>
      <span className="min-w-0 text-xl font-semibold leading-tight text-gray-900">{name}</span>
    </div>
  )
}

function eventLabel(key) {
  if (key === 'goals') return 'goal'
  if (key === 'assists') return 'assist'
  return key.replace(/_/g, ' ')
}

function HighlightPlayer({ game }) {
  return (
    <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
      <div
        className="absolute inset-0 overflow-hidden bg-black"
        style={{ background: 'radial-gradient(120% 90% at 30% 20%, #35524d 0%, #172026 55%, #0b0d10 100%)' }}
      >
        <span className="absolute inset-x-10 bottom-[36%] h-px bg-white/10" />
        <span className="absolute left-1/2 top-[14%] h-[66%] w-px bg-white/10" />
        <span className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-[60%] rounded-full border border-white/10" />
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-3 pb-3 pt-8">
          <span className="flex h-9 w-14 items-center justify-center rounded-md bg-[#00adef]">
            <span className="ml-0.5 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-white" />
          </span>
          <span className="relative h-1.5 flex-1 rounded-full bg-white/30">
            <span className="absolute inset-y-0 left-0 w-[18%] rounded-full bg-[#00adef]" />
          </span>
          <span className="rounded bg-black/40 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-white">{game.reel}</span>
        </div>
      </div>
    </div>
  )
}

function MatchReport({ game, view }) {
  const sport = game.sport
  const columns = PLAYER_COLUMNS[sport]
  return (
    <div className="overflow-x-hidden bg-gray-50 p-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <span className="flex items-center space-x-2 rounded-lg border border-gray-200 px-4 py-2.5 text-gray-600 shadow-sm">
              <ArrowLeft className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium">Back to Matches</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 shadow-sm">
              <Users className="h-3.5 w-3.5" />
              <span>Participants ({game.players.length})</span>
            </span>
            <span className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm">
              <Printer className="h-3.5 w-3.5" />
              <span>Print PDF</span>
            </span>
            <span className="inline-flex rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">Completed</span>
          </div>
        </div>

        <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white bg-gradient-to-b from-white to-gray-50/30 p-6 shadow-md">
          <div className="flex flex-col">
            <div className="mb-4 flex items-center justify-center gap-2">
              <span className="truncate text-xs font-semibold uppercase tracking-wide text-gray-500">{game.community}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="min-w-0 flex-1"><MatchTeamCrest name={game.home} align="right" /></div>
              <div className="flex-shrink-0 px-2 text-center">
                <div className="mb-1 text-4xl font-bold text-gray-900">{view.scoreA} - {view.scoreB}</div>
                <div className="text-sm text-gray-500">Full time</div>
              </div>
              <div className="min-w-0 flex-1"><MatchTeamCrest name={game.away} align="left" /></div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 border-t border-gray-100 pt-4 text-sm text-gray-600">
              <div className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gray-400" /><span>{fmtDate(game.datePlayed)}</span></div>
              <div className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-gray-400" /><span>{fmtTime(game.hourSlot)}</span></div>
              <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gray-400" /><span>{game.venueName} · {game.venue}</span></div>
            </div>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500">
                <Users className="h-4 w-4 text-[#0E1B4D]" />
                Players on this game ({game.players.length})
              </h4>
            </div>
            <RosterList roster={view.roster} grid />
          </div>

          {sport === 'football' ? (
            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="mb-4 flex items-center justify-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F2F3F5] text-[#0E1B4D]">
                  <Target className="h-4 w-4" />
                </div>
                <h4 className="text-xs font-semibold uppercase text-gray-500">Goal Scorers</h4>
              </div>
              <div className="grid grid-cols-3 items-start gap-6">
                <div className="col-span-1">
                  <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-blue-600">{game.home} goals</h4>
                  <div className="space-y-1">
                    {view.goals.A.map((e) => (
                      <div key={`${e.name}-${e.minute}`} className="text-sm text-gray-700">{e.name} <span className="text-gray-500">{e.minute}&apos;</span></div>
                    ))}
                  </div>
                </div>
                <div className="flex items-start justify-center px-4">
                  <div className="mt-6 h-3 w-3 rounded-full bg-gray-400" />
                </div>
                <div className="col-span-1 text-right">
                  <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{game.away} goals</h4>
                  <div className="space-y-1">
                    {view.goals.B.map((e) => (
                      <div key={`${e.name}-${e.minute}`} className="text-sm text-gray-700">{e.name} <span className="text-gray-500">{e.minute}&apos;</span></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <SectionIcon icon={Video} tone="bg-gradient-to-br from-[#0E1B4D] to-[#1B2438] text-white" />
              <h2 className="text-lg font-bold text-gray-900">Game Highlights</h2>
            </div>
            <span className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm">
              <Download className="h-4 w-4" />
              <span>Download</span>
            </span>
          </div>
          <HighlightPlayer game={game} />
        </div>

        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-row items-center justify-between gap-4">
            <div className="flex flex-1 items-center justify-start gap-3">
              <SectionIcon icon={Activity} tone="bg-[#D7F3E3] text-[#1B8A45]" />
              <h2 className="text-lg font-bold text-gray-900">Events</h2>
            </div>
            <div className="flex items-center justify-end gap-2">
              <span className="text-xs font-medium text-gray-600">Show:</span>
              <span className="flex items-center gap-3 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-slate-900">
                All Events
                <SbIcon name="chevronDown" size={12} className="text-gray-500" />
              </span>
            </div>
          </div>
          <div className="space-y-4">
            {view.events.map((e, i) => {
              const [icon, bg] = EVENT_INFO[e.key] || ['📊', 'bg-gray-100']
              const bubble = (
                <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${bg}`}>
                  <span className="text-xs text-white">{icon}</span>
                </div>
              )
              const label = (
                <span className={`truncate text-sm text-gray-700 ${e.side === 'A' ? 'text-right' : 'text-left'}`}>
                  {e.name} ({eventLabel(e.key)})
                </span>
              )
              return (
                <div key={`${e.minute}-${i}`} className="grid min-h-[2.5rem] grid-cols-[1fr_auto_1fr] items-center gap-4">
                  <div className="flex min-w-0 items-center justify-end space-x-3">
                    {e.side === 'A' ? <>{bubble}{label}</> : <div className="w-full" />}
                  </div>
                  <div className="flex flex-shrink-0 justify-center">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200">
                      <span className="text-xs font-medium text-gray-700">{e.minute}&apos;</span>
                    </div>
                  </div>
                  <div className="flex min-w-0 items-center justify-start space-x-3">
                    {e.side === 'B' ? <>{label}{bubble}</> : <div className="w-full" />}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center justify-center gap-3">
            <SectionIcon icon={BarChart2} tone="bg-gradient-to-br from-[#0E1B4D] to-[#1A2A66] text-white" />
            <h2 className="text-lg font-bold text-gray-900">Game Statistics</h2>
          </div>
          <div className="mx-auto max-w-2xl space-y-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-center gap-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-gray-100 to-amber-50 px-4 py-3">
              <div className="min-w-0 flex-1 text-center"><span className="block truncate text-sm font-semibold text-blue-700">{game.home}</span></div>
              <div className="w-36 flex-shrink-0 text-center"><span className="text-xs font-medium text-gray-600">Stat</span></div>
              <div className="min-w-0 flex-1 text-center"><span className="block truncate text-sm font-semibold text-amber-800">{game.away}</span></div>
            </div>
            {TEAM_METRICS[sport].map(([key, label, attemptedKey], rowIndex) => {
              const a = view.totalsA[key] ?? 0
              const b = view.totalsB[key] ?? 0
              const aAtt = attemptedKey ? view.totalsA[attemptedKey] : null
              const bAtt = attemptedKey ? view.totalsB[attemptedKey] : null
              const aPct = attemptedKey && aAtt > 0 ? Math.round((a / aAtt) * 100) : null
              const bPct = attemptedKey && bAtt > 0 ? Math.round((b / bAtt) * 100) : null
              return (
                <div key={key} className={`flex items-center justify-center gap-4 border-b border-gray-100 px-4 py-3 last:border-b-0 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/80'}`}>
                  <div className="min-w-0 flex-1 text-center">
                    <div className="mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-lg border border-blue-200/60 bg-blue-100">
                      <span className="text-lg font-bold leading-tight text-blue-700">{a}{attemptedKey ? `/${aAtt}` : ''}</span>
                      {aPct != null ? <span className="mt-0.5 text-[10px] font-semibold text-blue-600">{aPct}%</span> : null}
                    </div>
                  </div>
                  <div className="w-36 flex-shrink-0 text-center"><span className="block truncate text-sm font-medium text-gray-800">{label}</span></div>
                  <div className="min-w-0 flex-1 text-center">
                    <div className="mx-auto flex h-9 w-9 flex-col items-center justify-center rounded-lg border border-amber-200/60 bg-[#F2F3F5]">
                      <span className="text-lg font-bold leading-tight text-amber-800">{b}{attemptedKey ? `/${bAtt}` : ''}</span>
                      {bPct != null ? <span className="mt-0.5 text-[10px] font-semibold text-amber-700">{bPct}%</span> : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mb-6 overflow-visible rounded-lg border border-gray-200 bg-white p-4 shadow-md">
          <div className="mb-4 flex flex-row items-center justify-between gap-3">
            <div className="relative z-10 flex items-center gap-3">
              <SectionIcon icon={Users} tone="bg-gradient-to-br from-sky-500 to-blue-600 text-white" />
              <h2 className="text-lg font-bold text-gray-900">Player Performance</h2>
              <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-green-500 text-white shadow-lg">
                <Info className="h-4 w-4" />
              </span>
            </div>
            <span className="text-xs text-gray-400">Tap a row to open the player profile</span>
          </div>
          <div className="space-y-6">
            {view.sections.map((section) => (
              <div key={section.title} className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-bold uppercase tracking-wide text-gray-700">{section.title}</h3>
                  <span className="text-sm font-semibold text-gray-800">Avg grade: {section.avg.toFixed(1)}/10</span>
                </div>
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">Player</th>
                      {columns.map(([key, short]) => (
                        <th key={key} scope="col" className="px-0.5 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">{short}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {section.rows.map((p, index) => (
                      <tr key={p.name} className={index % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="px-2 py-2 align-middle">
                          <div className="flex min-w-0 items-center gap-2">
                            <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg text-xs font-bold text-white shadow-md ${avatarGradient(p.name)}`}>
                              {p.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">{p.name}</p>
                              <p className="truncate text-[11px] text-gray-500">{PROFILE_LINES[(p.name.length + index) % PROFILE_LINES.length]}</p>
                              <div className="mt-1">
                                <span className="inline-flex items-center whitespace-nowrap rounded-full border border-blue-500 px-2 py-1 text-[11px] font-medium text-blue-600">
                                  <Play className="mr-1 h-3 w-3" />
                                  View highlights
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        {columns.map(([key, , emphasize]) => {
                          const isGrade = key === 'grade'
                          const value = isGrade ? p.rating : statOf(sport, key, p.s)
                          const isTop = value === section.max[key]
                          const emphasized = emphasize && key !== 'saves' && value > 0
                          const tone = isTop
                            ? 'bg-gradient-to-r from-green-200 via-green-100 to-emerald-50 text-green-900 border border-green-300 shadow-sm'
                            : emphasized
                              ? 'bg-blue-50 text-blue-600 font-semibold'
                              : 'text-gray-700 font-semibold'
                          return (
                            <td key={key} className="px-0.5 py-2 text-center align-middle">
                              <span className={`inline-flex min-w-[1.75rem] justify-center whitespace-nowrap rounded-md px-1 py-1 text-xs font-semibold ${tone}`}>
                                {isGrade ? `${value.toFixed(1)}/10` : value}
                              </span>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function CommunityMatchPreview({ sport, width, height }) {
  const game = SAMPLE_GAMES[sport] || SAMPLE_GAMES.Football
  const view = VIEWS[sport] || VIEWS.Football
  const scrollRef = useRef(null)
  const zoom = (width - 2) / DESIGN_WIDTH
  const bodyHeight = height - 22

  const step = useCallback(() => {
    const node = scrollRef.current
    if (!node) return
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 4) node.scrollTo({ top: 0, behavior: 'smooth' })
    else node.scrollBy({ top: Math.round(node.clientHeight * 0.45), behavior: 'smooth' })
  }, [])
  const { ref, touched, interactionProps } = usePreviewAutoplay(step, 2600)

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden rounded-xl border border-[#d6d3d1] bg-white shadow-[0_24px_48px_-24px_rgba(7,16,24,0.45)] ${touched ? '' : 'sbx-anim'}`}
      style={{ width, animation: touched ? undefined : 'sbx-glow 2.4s ease-in-out infinite' }}
      {...interactionProps}
    >
      <PreviewHintStyles />
      {touched ? null : (
        <HintBubble style={{ left: '50%', bottom: 16, marginLeft: -66, zIndex: 5 }}>
          <span aria-hidden className="relative flex h-[18px] w-[12px] justify-center rounded-full border-[1.5px] border-[#0C0A09]">
            <span className="sbx-anim mt-[3px] h-[4px] w-[2px] rounded-full bg-[#0C0A09]" style={{ animation: 'sbx-scroll 1.4s ease-in-out infinite' }} />
          </span>
          Scroll to explore
        </HintBubble>
      )}
      <div className="flex h-[22px] items-center gap-2 border-b border-[#e7e5e4] bg-[#f5f5f4] px-2.5" aria-hidden>
        <span className="flex gap-1">
          <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
          <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
          <span className="h-2 w-2 rounded-full bg-[#28c840]" />
        </span>
        <span className="min-w-0 flex-1 truncate rounded-md bg-white px-2 py-[2px] text-center text-[9px] text-[#78716c]">
          statbox.co/community/match
        </span>
      </div>

      <div style={{ height: bodyHeight }} className="overflow-hidden">
        <div className="flex flex-col bg-[#F4F6F8] font-jakarta" style={{ width: DESIGN_WIDTH, height: bodyHeight / zoom, zoom }}>
          <PortalHeader />
          <div className="flex min-h-0 flex-1">
            <PortalSidebar communityName={game.community} />
            <div
              ref={scrollRef}
              className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none' }}
            >
              <main className="relative min-w-0 px-6 py-6">
                <PostGameHub game={game} view={view} />
                <MatchReport game={game} view={view} />
              </main>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
